import { MESSAGES, type MessageKey, type PluralKey } from './messages';
import { SERVER_PATTERNS, SERVER_TEXT } from './server';

/**
 * Language and formatting for the dashboard. Serbian (Latin script, sr-Latn-RS) is the default; English is the
 * alternative. The choice is kept in the viewer's browser.
 *
 * Interface text comes from the message catalogs in ./messages. Text the API sends (registry definitions, source
 * reasons, notes, limitations) is translated by ./server, keyed by the exact English text, so the API contract stays
 * unchanged. Identifiers (metric ids, dataset ids, column names, category and chain codes) are never translated.
 *
 * The active language is module state so plain functions (formatters, CSV builders) can read it; the provider keeps it
 * in sync and re-renders the tree when it changes.
 */

export type Lang = 'sr' | 'en';
export const LANGS: Lang[] = ['sr', 'en'];
export const DEFAULT_LANG: Lang = 'sr';
export const LOCALE: Record<Lang, string> = { sr: 'sr-Latn-RS', en: 'en-US' };
/** The value of the `lang` attribute on <html>. */
export const HTML_LANG: Record<Lang, string> = { sr: 'sr-Latn', en: 'en' };
export const STORAGE_KEY = 'aruha-analytics-lang';
const BELGRADE = 'Europe/Belgrade';

let active: Lang = DEFAULT_LANG;

export const getLang = (): Lang => active;
export function setActiveLang(lang: Lang) { active = lang; }

export function readStoredLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'en' || stored === 'sr' ? stored : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

export function storeLang(lang: Lang) {
  try { window.localStorage.setItem(STORAGE_KEY, lang); } catch { /* private mode or blocked storage: the choice lasts for this page only */ }
}

export type Vars = Record<string, string | number | null | undefined>;

function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars && vars[name] !== null && vars[name] !== undefined ? String(vars[name]) : whole));
}

/** Interface text in the active language. `{name}` placeholders are filled from `vars`. */
export function t(key: MessageKey, vars?: Vars): string {
  return fill(MESSAGES[active][key] ?? MESSAGES.en[key] ?? key, vars);
}

/**
 * A count with the right plural form: Serbian has three (1 red, 2 reda, 5 redova; 21 red, 22 reda), English two.
 * `{count}` in the message is the formatted number.
 */
export function plural(key: PluralKey, count: number, vars?: Vars): string {
  const rule = new Intl.PluralRules(LOCALE[active]).select(count);
  const form = (rule === 'one' || rule === 'few' ? rule : 'other') as 'one' | 'few' | 'other';
  const full = `${key}_${form}` as MessageKey;
  return t(full, { count: formatInteger(count), ...vars });
}

/** API text (reasons, notes, definitions) in the active language; unknown text is shown as sent. */
export function tx(text: string): string;
export function tx(text: string | null | undefined): string | null | undefined;
export function tx(text: string | null | undefined): string | null | undefined {
  if (!text || active === 'en') return text;
  const exact = SERVER_TEXT[text];
  if (exact !== undefined) return exact;
  for (const [pattern, replace] of SERVER_PATTERNS) {
    const match = pattern.exec(text);
    if (match) return replace(match);
  }
  return text;
}

/** True when `tx` has a Serbian translation for the text (used by the coverage tests). */
export function hasServerTranslation(text: string): boolean {
  return SERVER_TEXT[text] !== undefined || SERVER_PATTERNS.some(([pattern]) => pattern.test(text));
}

// ─── Formatting ──────────────────────────────────────────────────────────────

const locale = () => LOCALE[active];

export function formatNumber(value: number, maximumFractionDigits = 2, minimumFractionDigits = 0): string {
  return new Intl.NumberFormat(locale(), { maximumFractionDigits, minimumFractionDigits }).format(value);
}

export const formatInteger = (value: number) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 0 }).format(value);

/** 12.5 -> "12,5%" (sr) or "12.5%" (en). The value is already a percentage. */
export const formatPercentValue = (value: number, digits = 1) => `${formatNumber(value, digits)}%`;

/** Percentage points, for the change of a rate. */
export const formatPoints = (value: number) => `${formatNumber(value, 1)} ${active === 'sr' ? 'p. p.' : 'pp'}`;

/** Money. In Serbian the currency code follows the amount after a no-break space ("58,76 USD"); in English the symbol leads ("$58.76"). */
export function formatMoney(value: number, currency = 'USD', maximumFractionDigits = 2): string {
  if (currency === 'RSD') return `${formatNumber(value, maximumFractionDigits)}\u00a0RSD`;
  return new Intl.NumberFormat(locale(), { style: 'currency', currency, currencyDisplay: active === 'sr' ? 'code' : 'symbol', maximumFractionDigits }).format(value);
}

/** A calendar date (YYYY-MM-DD): "17.10.2026." in Serbian, unchanged ISO in English. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  return active === 'sr' ? `${match[3]}.${match[2]}.${match[1]}.` : iso;
}

/** A short day for chart axes: "17.10." or "10-17". */
export function formatShortDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  return active === 'sr' ? `${match[3]}.${match[2]}.` : `${match[2]}-${match[3]}`;
}

/** A timestamp as a Belgrade date and time; a bare date stays a date. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(active === 'sr' ? 'sr-Latn-RS' : 'en-GB', { timeZone: BELGRADE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
}

/** "2026-10" or "2026-10-01" as "oktobar 2026." or "October 2026". */
export function formatMonth(iso: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return new Intl.DateTimeFormat(active === 'sr' ? 'sr-Latn-RS' : 'en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}
